/**
 * Watches the local microphone for a mute this app cannot otherwise see — the
 * OS mic toggle, a headset's hardware button, the OS reclaiming the device —
 * and reports it so the person can be told. See `mic-silence` for the rules; the
 * whole design rationale (why two signals, why digital silence and not merely
 * "quiet") lives there.
 *
 * This half is the part that cannot be pure: it attaches the track's
 * `mute`/`unmute`/`ended` listeners and runs a small always-on analyser that
 * reads the RAW microphone's level. It must be the raw capture track, never the
 * producer's track: with noise cancellation on, the producer carries a
 * WebAudio-processed track, and the OS-mute state lives on the capture.
 *
 * It never pauses the producer and never touches the SFU — unlike its sibling
 * `camera-interruption`, which pauses video for the room. A muted mic that the
 * user thinks is live is a thing to warn about, not to act on behind their back.
 */

import { getAudioContextCtor } from '@/features/devices/utils/device-permissions'
import {
  createMicSilenceDetector,
  DEFAULT_MIC_SILENCE_CONFIG,
  type MicSilenceConfig,
} from './mic-silence'

/** How often the silence sampler reads the mic. Mirrors noise-cancellation.ts. */
const CHECK_MS = 500
/** Samples per reading. Power of two; ~85 ms at 48 kHz — a steady RMS window. */
const METER_FFT = 4096

/** Reads one raw-mic RMS level, in dBFS, or null when it cannot (context not running). */
export type MicMeter = {
  readDbfs(): number | null
  close(): void
}

export type MicInterruptionOptions = {
  /** Called whenever the system-mute verdict flips. */
  onChange: (systemMicMuted: boolean) => void
  /** True only when the app believes the mic is live: micOn AND the producer is not paused. */
  isLive: () => boolean
  config?: Partial<MicSilenceConfig>
  checkMs?: number
  // Injectables, for tests.
  now?: () => number
  setIntervalFn?: (fn: () => void, ms: number) => unknown
  clearIntervalFn?: (handle: unknown) => void
  makeMeter?: (track: MediaStreamTrack) => MicMeter | null
}

export type MicInterruption = {
  /** Watch this microphone from now on. Call again whenever the device manager swaps it. */
  setTrack(track: MediaStreamTrack | null): void
  readonly muted: boolean
  dispose(): void
}

/** Default analyser-backed meter over the raw mic track. */
function makeAnalyserMeter(track: MediaStreamTrack): MicMeter | null {
  const Ctor = getAudioContextCtor()
  if (!Ctor) return null
  let ctx: AudioContext
  try {
    ctx = new Ctor()
  } catch {
    return null
  }
  // In a meeting the page has been interacted with, so the context should
  // resume; a suspended one is reported as "no reading", never as silence.
  void ctx.resume?.().catch(() => {})
  const source = ctx.createMediaStreamSource(new MediaStream([track]))
  const analyser = ctx.createAnalyser()
  analyser.fftSize = METER_FFT
  source.connect(analyser)
  const buf = new Float32Array(analyser.fftSize)

  return {
    readDbfs() {
      // A suspended/closed context holds stale readings — refuse to read it, so
      // the silence path never advances on a frozen meter.
      if (ctx.state !== 'running') return null
      analyser.getFloatTimeDomainData(buf)
      let sum = 0
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i]
      const rms = Math.sqrt(sum / buf.length)
      return rms > 0 ? 20 * Math.log10(rms) : -Infinity
    },
    close() {
      try {
        source.disconnect()
      } catch {
        // already gone
      }
      void ctx.close?.().catch(() => {})
    },
  }
}

export function createMicInterruption(opts: MicInterruptionOptions): MicInterruption {
  const now =
    opts.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()))
  const setI = opts.setIntervalFn ?? ((fn, ms) => setInterval(fn, ms))
  const clearI =
    opts.clearIntervalFn ?? ((h) => clearInterval(h as ReturnType<typeof setInterval>))
  const makeMeter = opts.makeMeter ?? makeAnalyserMeter
  const detector = createMicSilenceDetector({ ...DEFAULT_MIC_SILENCE_CONFIG, ...opts.config })

  let track: MediaStreamTrack | null = null
  let meter: MicMeter | null = null
  let disposed = false
  let emitted = false

  const emit = (v: boolean) => {
    if (v === emitted) return
    emitted = v
    opts.onChange(v)
  }

  const tick = () => {
    if (disposed) return
    const t = track
    const gated = !!t && opts.isLive() && t.readyState === 'live'
    const rmsDbfs = gated && meter ? meter.readDbfs() : null
    const trackMuted = !!t && t.muted
    emit(detector.update({ now: now(), gated, trackMuted, rmsDbfs }))
  }

  // The track's own mute/unmute/ended are instant where the UA fires them; the
  // interval catches every platform that does not, via the silence meter.
  const onEvent = () => tick()

  const attach = () => {
    if (!track) return
    track.addEventListener('mute', onEvent)
    track.addEventListener('unmute', onEvent)
    track.addEventListener('ended', onEvent)
  }
  const detach = () => {
    if (!track) return
    track.removeEventListener('mute', onEvent)
    track.removeEventListener('unmute', onEvent)
    track.removeEventListener('ended', onEvent)
  }

  const interval = setI(tick, opts.checkMs ?? CHECK_MS)

  return {
    setTrack(next) {
      if (disposed || next === track) return
      detach()
      meter?.close()
      meter = null
      track = next
      if (track) {
        attach()
        meter = makeMeter(track)
      }
      detector.reset()
      tick()
    },
    get muted() {
      return emitted
    },
    dispose() {
      if (disposed) return
      disposed = true
      clearI(interval)
      detach()
      meter?.close()
      meter = null
      track = null
    },
  }
}
